import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect, useImperativeHandle, type Ref } from "react";
import { z } from "zod";
import type {
  CreateAssignmentDTO,
  EventKind,
  EventShowDTO,
  RoleFeeKind,
  UpdateAssignmentDTO,
} from "../../domain/contracts";
import {
  isMusicArrangerShowBaseAmount,
  MUSIC_ARRANGER_SHOW_BASE_AMOUNTS,
  ROLE_FEE_KIND_LABELS,
  RoleFeeKindSchema,
} from "../../domain/contracts";
import { AssignmentShowPicker } from "./AssignmentShowPicker";

type PerformerItem = {
  id: string;
  name: string;
  defaultRoleFeeKind?: RoleFeeKind;
};

const ROLE_FEE_KIND_OPTIONS: Array<{ value: RoleFeeKind; label: string }> = (
  Object.entries(ROLE_FEE_KIND_LABELS) as Array<[RoleFeeKind, string]>
).map(([value, label]) => ({ value, label }));

const MUSIC_ARRANGER_RATE_OPTIONS = MUSIC_ARRANGER_SHOW_BASE_AMOUNTS.map(
  (amount) => ({
    value: String(amount),
    label: `${amount.toLocaleString("lo-LA")} ₭`,
  }),
);

const AssignmentFormSchema = z
  .object({
    performerId: z.string().optional(),
    roleFeeKind: RoleFeeKindSchema,
    showCount: z.number().int().min(1, "ຈຳນວນໂຊຕ້ອງ ≥ 1"),
    rehearsalDays: z.number().int().min(0, "ຈຳນວນວັນຊ້ອມຕ້ອງ ≥ 0"),
    showBaseAmountOverride: z.coerce.number().int().positive().optional(),
    lineKey: z.string().optional(),
    notes: z.string().nullable().optional(),
    eventShowIds: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.eventShowIds != null && data.eventShowIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 ໂຊ",
        path: ["eventShowIds"],
      });
    }
    if (data.roleFeeKind === "music_arranger") {
      if (
        data.showBaseAmountOverride == null ||
        !isMusicArrangerShowBaseAmount(data.showBaseAmountOverride)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງເລືອກອັດຕາຄ່າຕັດແຕ່ງເພງ",
          path: ["showBaseAmountOverride"],
        });
      }
    }
  });

type AssignmentFormValues = z.infer<typeof AssignmentFormSchema>;

export type AssignmentRulePayload = Omit<CreateAssignmentDTO, "performerId">;

type AssignmentFormProps = {
  eventKind?: EventKind;
  /** Event schedule show count — used as default / max hint. */
  derivedShowCount?: number;
  /** Full event show list — enables per-show selection when length >= 2. */
  eventShows?: EventShowDTO[];
  initialValues?: Partial<AssignmentFormValues>;
  onSubmit: (values: CreateAssignmentDTO | UpdateAssignmentDTO) => void;
  onSubmitRulePayload?: (payload: AssignmentRulePayload) => void;
  onRulePayloadChange?: (payload: AssignmentRulePayload | null) => void;
  submitting?: boolean;
  onCancel?: () => void;
  mode?: "create" | "edit";
  variant?: "single" | "bulk";
  compact?: boolean;
  hideActions?: boolean;
  submitRef?: Ref<{ submit: () => void } | null>;
};

export function buildAssignmentRulePayload(
  vals: AssignmentFormValues,
  options?: { useEventShowIds?: boolean },
): AssignmentRulePayload {
  const payload: AssignmentRulePayload = {
    roleFeeKind: vals.roleFeeKind,
    showCount: vals.showCount,
    rehearsalDays: vals.rehearsalDays,
    lineKey: vals.lineKey?.trim() || "default",
    notes: vals.notes ?? null,
  };
  if (options?.useEventShowIds && vals.eventShowIds && vals.eventShowIds.length > 0) {
    payload.eventShowIds = vals.eventShowIds;
    delete payload.showCount;
  }
  if (vals.roleFeeKind === "music_arranger" && vals.showBaseAmountOverride) {
    payload.showBaseAmountOverride = Number(vals.showBaseAmountOverride);
  }
  return payload;
}

function performerLookupUrl(eventKind: EventKind) {
  if (eventKind === "kol") {
    return `${config.apiUrl}/performers/lookup?performerType=kol`;
  }
  return `${config.apiUrl}/performers/lookup`;
}

export function AssignmentForm({
  eventKind = "stage_show",
  derivedShowCount,
  eventShows,
  initialValues,
  onSubmit,
  onSubmitRulePayload,
  onRulePayloadChange,
  submitting,
  onCancel,
  mode = "create",
  variant = "single",
  compact = false,
  hideActions = false,
  submitRef,
}: AssignmentFormProps) {
  const defaultShowCount = Math.max(
    1,
    initialValues?.showCount ?? derivedShowCount ?? 1,
  );
  const showPickerEnabled = (eventShows?.length ?? 0) >= 2;
  const defaultEventShowIds =
    initialValues?.eventShowIds ??
    (showPickerEnabled ? eventShows!.map((show) => show.id) : undefined);
  const maxShowCount =
    derivedShowCount != null ? Math.max(1, derivedShowCount) : undefined;
  const initialRole = initialValues?.roleFeeKind ?? "performer";

  const methods = RHF.useForm<AssignmentFormValues>({
    resolver: zodResolver(AssignmentFormSchema),
    defaultValues: {
      performerId: initialValues?.performerId ?? "",
      roleFeeKind: initialRole,
      showCount: defaultShowCount,
      rehearsalDays: initialValues?.rehearsalDays ?? 0,
      showBaseAmountOverride:
        initialValues?.showBaseAmountOverride != null
          ? String(initialValues.showBaseAmountOverride)
          : initialRole === "music_arranger"
            ? String(MUSIC_ARRANGER_SHOW_BASE_AMOUNTS[0])
            : undefined,
      lineKey: initialValues?.lineKey ?? "default",
      notes: initialValues?.notes ?? null,
      eventShowIds: defaultEventShowIds,
    },
  });

  const watchedEventShowIds = methods.watch("eventShowIds");
  const derivedSelectedShowCount = Math.max(
    1,
    watchedEventShowIds?.length ?? defaultShowCount,
  );

  useEffect(() => {
    if (showPickerEnabled) {
      methods.setValue("showCount", derivedSelectedShowCount);
    }
  }, [derivedSelectedShowCount, methods, showPickerEnabled]);

  const watchedPerformerId = methods.watch("performerId");
  const watchedRoleFeeKind = methods.watch("roleFeeKind");

  useEffect(() => {
    if (watchedRoleFeeKind === "music_arranger") {
      const currentRate = methods.getValues("showBaseAmountOverride");
      if (
        currentRate == null ||
        !isMusicArrangerShowBaseAmount(Number(currentRate))
      ) {
        methods.setValue(
          "showBaseAmountOverride",
          String(MUSIC_ARRANGER_SHOW_BASE_AMOUNTS[0]),
        );
      }
      if (methods.getValues("showCount") !== 1) {
        methods.setValue("showCount", 1);
      }
      if (showPickerEnabled && methods.getValues("eventShowIds")?.length !== 1) {
        const first = eventShows?.[0]?.id;
        if (first) methods.setValue("eventShowIds", [first]);
      }
      return;
    }
    methods.setValue("showBaseAmountOverride", undefined);
  }, [watchedRoleFeeKind, methods, showPickerEnabled, eventShows]);

  useEffect(() => {
    if (mode !== "create" || variant !== "single" || !watchedPerformerId) return;
    let cancelled = false;
    void hydrateLookupItem<PerformerItem>(
      performerLookupUrl(eventKind),
      watchedPerformerId,
    ).then((item) => {
      if (cancelled || !item?.defaultRoleFeeKind) return;
      methods.setValue("roleFeeKind", item.defaultRoleFeeKind);
    });
    return () => {
      cancelled = true;
    };
  }, [watchedPerformerId, mode, variant, eventKind, methods]);

  useEffect(() => {
    if (variant !== "bulk" || !onRulePayloadChange) return;
    const subscription = methods.watch((vals) => {
      if (!vals.roleFeeKind || !vals.showCount || vals.showCount < 1) {
        onRulePayloadChange(null);
        return;
      }
      if (
        showPickerEnabled &&
        (!vals.eventShowIds || vals.eventShowIds.length === 0)
      ) {
        onRulePayloadChange(null);
        return;
      }
      if (
        vals.roleFeeKind === "music_arranger" &&
        (vals.showBaseAmountOverride == null ||
          !isMusicArrangerShowBaseAmount(Number(vals.showBaseAmountOverride)))
      ) {
        onRulePayloadChange(null);
        return;
      }
      onRulePayloadChange(
        buildAssignmentRulePayload(vals as AssignmentFormValues, {
          useEventShowIds: showPickerEnabled,
        }),
      );
    });
    return () => subscription.unsubscribe();
  }, [methods, onRulePayloadChange, variant, showPickerEnabled]);

  useImperativeHandle(
    submitRef,
    () => ({
      submit: () => {
        void methods.handleSubmit((vals) => {
          const payload = buildAssignmentRulePayload(vals, {
            useEventShowIds: showPickerEnabled,
          });
          if (mode === "create" && variant === "bulk") {
            onSubmitRulePayload?.(payload);
            return;
          }
          if (mode === "create") {
            onSubmit({ performerId: vals.performerId ?? "", ...payload });
          } else {
            onSubmit(payload);
          }
        })();
      },
    }),
    [methods, mode, onSubmit, onSubmitRulePayload, variant, showPickerEnabled],
  );

  return (
    <FormRoot
      methods={methods}
      onSubmit={(vals) => {
        const payload = buildAssignmentRulePayload(vals, {
          useEventShowIds: showPickerEnabled,
        });
        if (mode === "create" && variant === "bulk") {
          onSubmitRulePayload?.(payload);
          return;
        }
        if (mode === "create") {
          onSubmit({ performerId: vals.performerId ?? "", ...payload });
        } else {
          onSubmit(payload);
        }
      }}
      className={compact ? "space-y-2" : "space-y-4"}
    >
      {mode === "create" && variant === "single" && (
        <div>
          <p className="mb-1.5 font-medium text-sm">
            ນັກສະແດງ <span className="text-destructive">*</span>
          </p>
          <FormInfiniteCombobox<PerformerItem>
            name="performerId"
            placeholder="ຄົ້ນຫານັກສະແດງ..."
            queryKey={["performance", "performers", "lookup", eventKind]}
            queryFn={({ search, pageParam }) =>
              fetchLookupForInfinite<PerformerItem>(
                performerLookupUrl(eventKind),
                {
                  search,
                  pageParam,
                },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<PerformerItem>(
                performerLookupUrl(eventKind),
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
          />
        </div>
      )}

      <div
        className={
          compact
            ? "grid grid-cols-12 items-end gap-2"
            : "grid gap-4 sm:grid-cols-2"
        }
      >
        <div className={compact ? "col-span-12 sm:col-span-5" : undefined}>
          <FormSelect
            name="roleFeeKind"
            label="ບົດບາດ"
            options={ROLE_FEE_KIND_OPTIONS}
          />
        </div>
        {watchedRoleFeeKind === "music_arranger" ? (
          <div className={compact ? "col-span-12 sm:col-span-4" : undefined}>
            <FormSelect
              name="showBaseAmountOverride"
              label="ອັດຕາຄ່າຕັດແຕ່ງເພງ"
              options={MUSIC_ARRANGER_RATE_OPTIONS}
            />
          </div>
        ) : showPickerEnabled ? (
          <div className={compact ? "col-span-12 sm:col-span-3" : undefined}>
            <p className="mb-1 font-medium text-sm">ຈຳນວນໂຊ</p>
            <p className="rounded-md border bg-muted/20 px-3 py-2 text-sm">
              {derivedSelectedShowCount} ໂຊ (ຈາກການເລືອກ)
            </p>
          </div>
        ) : (
          <div className={compact ? "col-span-6 sm:col-span-3" : undefined}>
            <FormNumber
              name="showCount"
              label="ຈຳນວນໂຊ"
              placeholder="1"
              min={1}
              max={maxShowCount}
            />
            {maxShowCount != null && !compact ? (
              <p className="mt-1 text-muted-foreground text-xs">
                ຕາຕະລາງງານ {maxShowCount} ໂຊ — ແກ້ໄຂໄດ້ຖ້າຄົນນີ້ເຮັດບໍ່ຄົບ
              </p>
            ) : null}
          </div>
        )}
        <div className={compact ? "col-span-6 sm:col-span-3" : undefined}>
          <FormNumber
            name="rehearsalDays"
            label="ວັນຊ້ອມ"
            placeholder="0"
            min={0}
            disabled={watchedRoleFeeKind === "music_arranger"}
          />
        </div>
        <div className={compact ? "col-span-12 sm:col-span-1" : undefined}>
          <FormInput name="lineKey" label="Key" placeholder="default" />
        </div>
      </div>

      {showPickerEnabled && eventShows ? (
        <AssignmentShowPicker
          eventShows={eventShows}
          selectedIds={watchedEventShowIds ?? []}
          singleSelect={watchedRoleFeeKind === "music_arranger"}
          disabled={submitting}
          onChange={(ids) => methods.setValue("eventShowIds", ids)}
        />
      ) : null}

      {compact ? (
        <FormInput
          name="notes"
          label="ໝາຍເຫດ"
          placeholder="ບັນທຶກເພີ່ມເຕີມ (ບໍ່ບັງຄັບ)"
        />
      ) : (
        <FormTextarea
          name="notes"
          label="ໝາຍເຫດ"
          placeholder="ບັນທຶກເພີ່ມເຕີມ"
          rows={2}
        />
      )}

      {!hideActions && (
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              ຍົກເລີກ
            </Button>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </div>
      )}
    </FormRoot>
  );
}
